/**
 * Materialized global-variable references in inline text (`[[gv:…]]`).
 * Region SSR renders `displayValue` only — no registry lookup at request time.
 */

export type GlobalVariableYearMode = "live" | "tax_year" | "as_at_publication";

export type GlobalVariableRefSyncStatus = "synced" | "pending" | "failed";

export type GlobalVariableTokenKind = "cell" | "row" | "table";

/** Compact persisted payload (base64url inside `[[gv:…]]`). */
export type MaterializedGlobalVariableRefPayload = {
	/** displayValue */
	dv: string;
	/** rateSetKey */
	rs: string;
	/** rowRef (slug or #rowId) */
	rr?: string;
	/** columnKey */
	ck?: string;
	/** tokenKind */
	tk: GlobalVariableTokenKind;
	/** yearMode */
	ym: GlobalVariableYearMode;
	/** taxYear when year-pinned */
	ty?: string;
	/** syncStatus */
	ss?: GlobalVariableRefSyncStatus;
	/** resolvedAt ISO */
	ra?: string;
	/** registryVersionId */
	rv?: string;
	/** legacy/debug token string */
	tok?: string;
};

export type MaterializedGlobalVariableRef = {
	source: {
		rateSetKey: string;
		rowRef?: string;
		columnKey?: string;
	};
	tokenKind: GlobalVariableTokenKind;
	yearMode: GlobalVariableYearMode;
	taxYear?: string;
	displayValue: string;
	syncStatus?: GlobalVariableRefSyncStatus;
	resolvedAt?: string;
	registryVersionId?: string;
	/** Optional legacy `{{…}}` token for debugging / lint */
	token?: string;
};

const GV_REF_RE = /\[\[gv:([A-Za-z0-9_-]+)\]\]/g;

function toStandardBase64FromUtf8(input: string): string {
	if (typeof Buffer !== "undefined") {
		return Buffer.from(input, "utf8").toString("base64");
	}
	const bytes = new TextEncoder().encode(input);
	let binary = "";
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	return btoa(binary);
}

function fromStandardBase64ToUtf8(b64: string): string {
	if (typeof Buffer !== "undefined") {
		return Buffer.from(b64, "base64").toString("utf8");
	}
	const binary = atob(b64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return new TextDecoder().decode(bytes);
}

function base64UrlEncodeUtf8(input: string): string {
	return toStandardBase64FromUtf8(input)
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
}

function base64UrlDecodeUtf8(encoded: string): string {
	const padded = encoded.replace(/-/g, "+").replace(/_/g, "/");
	const pad = (4 - (padded.length % 4)) % 4;
	return fromStandardBase64ToUtf8(padded + "=".repeat(pad));
}

export function payloadToRef(
	payload: MaterializedGlobalVariableRefPayload
): MaterializedGlobalVariableRef {
	return {
		source: {
			rateSetKey: payload.rs,
			...(payload.rr ? { rowRef: payload.rr } : {}),
			...(payload.ck ? { columnKey: payload.ck } : {}),
		},
		tokenKind: payload.tk,
		yearMode: payload.ym,
		...(payload.ty ? { taxYear: payload.ty } : {}),
		displayValue: payload.dv,
		...(payload.ss ? { syncStatus: payload.ss } : {}),
		...(payload.ra ? { resolvedAt: payload.ra } : {}),
		...(payload.rv ? { registryVersionId: payload.rv } : {}),
		...(payload.tok ? { token: payload.tok } : {}),
	};
}

export function refToPayload(
	ref: MaterializedGlobalVariableRef
): MaterializedGlobalVariableRefPayload {
	return {
		dv: ref.displayValue,
		rs: ref.source.rateSetKey,
		...(ref.source.rowRef ? { rr: ref.source.rowRef } : {}),
		...(ref.source.columnKey ? { ck: ref.source.columnKey } : {}),
		tk: ref.tokenKind,
		ym: ref.yearMode,
		...(ref.taxYear ? { ty: ref.taxYear } : {}),
		...(ref.syncStatus ? { ss: ref.syncStatus } : {}),
		...(ref.resolvedAt ? { ra: ref.resolvedAt } : {}),
		...(ref.registryVersionId ? { rv: ref.registryVersionId } : {}),
		...(ref.token ? { tok: ref.token } : {}),
	};
}

export function encodeMaterializedGlobalVariableRef(
	ref: MaterializedGlobalVariableRef
): string {
	const payload = refToPayload(ref);
	return `[[gv:${base64UrlEncodeUtf8(JSON.stringify(payload))}]]`;
}

export function decodeMaterializedGlobalVariableRef(
	raw: string
): MaterializedGlobalVariableRef | null {
	const trimmed = raw.trim();
	const match = /^\[\[gv:([A-Za-z0-9_-]+)\]\]$/.exec(trimmed);
	if (!match?.[1]) {
		return null;
	}
	try {
		const json = base64UrlDecodeUtf8(match[1]);
		const parsed = JSON.parse(json) as MaterializedGlobalVariableRefPayload;
		if (
			typeof parsed.rs !== "string" ||
			typeof parsed.dv !== "string" ||
			(parsed.tk !== "cell" && parsed.tk !== "row" && parsed.tk !== "table") ||
			(parsed.ym !== "live" &&
				parsed.ym !== "tax_year" &&
				parsed.ym !== "as_at_publication")
		) {
			return null;
		}
		return payloadToRef(parsed);
	} catch {
		return null;
	}
}

export type ParsedMaterializedGlobalVariableRef = {
	end: number;
	raw: string;
	ref: MaterializedGlobalVariableRef;
	start: number;
};

export function parseMaterializedGlobalVariableRefs(
	content: string
): ParsedMaterializedGlobalVariableRef[] {
	const results: ParsedMaterializedGlobalVariableRef[] = [];
	GV_REF_RE.lastIndex = 0;
	let match: RegExpExecArray | null = GV_REF_RE.exec(content);
	while (match !== null) {
		const raw = match[0];
		const ref = decodeMaterializedGlobalVariableRef(raw);
		if (ref) {
			results.push({
				end: match.index + raw.length,
				raw,
				ref,
				start: match.index,
			});
		}
		match = GV_REF_RE.exec(content);
	}
	return results;
}

/** Replace materialized refs with display values (SSR / public render). */
export function renderMaterializedGlobalVariableRefsInText(
	content: string
): string {
	if (!content.includes("[[gv:")) {
		return content;
	}
	const parsed = parseMaterializedGlobalVariableRefs(content);
	if (parsed.length === 0) {
		return content;
	}
	let out = "";
	let cursor = 0;
	for (const hit of parsed) {
		out += content.slice(cursor, hit.start);
		out += hit.ref.displayValue;
		cursor = hit.end;
	}
	out += content.slice(cursor);
	return out;
}

export function formatRegistryCellDisplayValue(
	value: unknown,
	formatHint?: "percent" | "amount" | "text"
): string {
	if (value === null || value === undefined) {
		return "";
	}
	const raw = String(value).trim();
	if (!raw) {
		return "";
	}
	if (formatHint === "percent") {
		return raw.includes("%") ? raw : `${raw}%`;
	}
	if (formatHint === "amount") {
		return raw.startsWith("£") ? raw : `£${raw}`;
	}
	return raw;
}

export function isYearPinnedRef(ref: MaterializedGlobalVariableRef): boolean {
	return (
		(ref.yearMode === "tax_year" || ref.yearMode === "as_at_publication") &&
		Boolean(ref.taxYear?.trim())
	);
}

export function buildMaterializedRefFromPicker(params: {
	rateSetKey: string;
	rowRef?: string;
	columnKey?: string;
	tokenKind: GlobalVariableTokenKind;
	displayValue: string;
	token?: string;
	yearMode?: GlobalVariableYearMode;
	taxYear?: string;
	registryVersionId?: string;
	syncStatus?: GlobalVariableRefSyncStatus;
}): MaterializedGlobalVariableRef {
	return {
		source: {
			rateSetKey: params.rateSetKey,
			...(params.rowRef ? { rowRef: params.rowRef } : {}),
			...(params.columnKey ? { columnKey: params.columnKey } : {}),
		},
		tokenKind: params.tokenKind,
		yearMode: params.yearMode ?? "live",
		...(params.taxYear ? { taxYear: params.taxYear } : {}),
		displayValue: params.displayValue,
		syncStatus: params.syncStatus ?? "synced",
		...(params.registryVersionId
			? { registryVersionId: params.registryVersionId }
			: {}),
		resolvedAt: new Date().toISOString(),
		...(params.token ? { token: params.token } : {}),
	};
}
